import React from 'react';
import { Menu, Typography, message } from 'antd';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  DashboardOutlined,
  FileTextOutlined,
  BarChartOutlined,
  RocketOutlined,
  TeamOutlined,
  DollarOutlined,
  CheckCircleOutlined,
  TrophyOutlined,
  FilterOutlined,
  HomeOutlined,
  ProjectOutlined,
  UserOutlined,
  MailOutlined,
  SettingOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '../../store';
import { VersionInfo } from '../../utils/version';

const { Text } = Typography;

interface SideNavigationProps {
  stage: string;
  collapsed?: boolean;
}

const SideNavigation: React.FC<SideNavigationProps> = ({
  stage,
  collapsed = false,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  // 管理员专用菜单配置
  const getAdminMenuItems = () => [
    {
      key: 'workspace',
      label: t('menu:projectWorkbench'),
      type: 'group' as const,
      children: [
        {
          key: '/admin/screening',
          label: t('menu:screening'),
          icon: <FilterOutlined />,
        },
        {
          key: '/admin/project-management',
          label: t('menu:projectManagement'),
          icon: <ProjectOutlined />,
        },
      ],
    },
    {
      key: 'system',
      label: t('menu:systemSettings'),
      type: 'group' as const,
      children: [
        {
          key: '/admin/user-management',
          label: t('menu:userManagement'),
          icon: <UserOutlined />,
        },
        {
          key: '/admin/channel-management',
          label: t('menu:channelManagement'),
          icon: <TeamOutlined />,
        },
      ],
    },
    {
      key: 'settings',
      label: t('menu:settings'),
      type: 'group' as const,
      children: [
        {
          key: '/admin/system-settings/email',
          label: t('menu:emailSettings'),
          icon: <MailOutlined />,
        },
      ],
    },
    {
      key: 'analytics',
      label: t('menu:analytics'),
      type: 'group' as const,
      children: [
        {
          key: '/admin/analytics/dashboard',
          label: t('menu:dashboard'),
          icon: <BarChartOutlined />,
        },
        {
          key: '/admin/analytics/reports',
          label: t('menu:reports'),
          icon: <FileTextOutlined />,
        },
      ],
    },
  ];

  // 交易所专用菜单配置（参考发行人菜单结构）
  const getExchangeMenuItems = () => [
    {
      key: 'workspace',
      label: t('menu:projectWorkbench'),
      type: 'group' as const,
      children: [
        {
          key: '/admin/screening',
          label: t('menu:screening'),
          icon: <FilterOutlined />,
        },
        {
          key: '/admin/project-management',
          label: t('menu:projectManagement'),
          icon: <ProjectOutlined />,
        },
      ],
    },
    {
      key: 'system',
      label: t('menu:systemSettings'),
      type: 'group' as const,
      children: [
        {
          key: '/admin/user-management',
          label: t('menu:userManagement'),
          icon: <UserOutlined />,
        },
        {
          key: '/admin/channel-management',
          label: t('menu:channelManagement'),
          icon: <TeamOutlined />,
        },
      ],
    },
  ];

  // 需要提示暂无权限的菜单路径
  const restrictedMenuPaths = [
    '/admin/issuance/process',
    '/admin/issuance/documents',
    '/admin/issuance/compliance',
  ];

  // 发行人用户菜单配置
  const getIssuerMenuItems = () => [
    {
      key: 'workspace',
      label: t('menu:projectWorkbench'),
      type: 'group' as const,
      children: [
        {
          key: '/admin/issuer/questionnaires',
          label: t('menu:myQuestionnaires'),
          icon: <FileTextOutlined />,
        },
        {
          key: '/admin/project-management',
          label: t('menu:projectManagement'),
          icon: <ProjectOutlined />,
        },
      ],
    },
    {
      key: 'issuance',
      label: t('menu:issuance'),
      type: 'group' as const,
      children: [
        {
          key: '/admin/issuance/process',
          label: t('menu:workflow'),
          icon: <RocketOutlined />,
        },
        {
          key: '/admin/issuance/documents',
          label: t('menu:documents'),
          icon: <FileTextOutlined />,
        },
        {
          key: '/admin/issuance/compliance',
          label: t('menu:ratingCertification'),
          icon: <CheckCircleOutlined />,
        },
      ],
    },
  ];

  // 企业用户菜单配置
  const getCompanyMenuItems = (currentStage: string) => {
    const configs = {
      assessment: [
        {
          key: 'assessment',
          label: t('menu:assessment.title'),
          type: 'group' as const,
          children: [
            {
              key: '/admin/assessment/quick',
              label: t('menu:assessment.quick'),
              icon: <RocketOutlined />,
            },
            {
              key: '/admin/assessment/dashboard',
              label: t('menu:assessment.dashboard'),
              icon: <DashboardOutlined />,
            },
            {
              key: '/admin/assessment/improvement',
              label: t('menu:assessment.improvement'),
              icon: <TrophyOutlined />,
            },
            {
              key: '/admin/assessment/progress',
              label: t('menu:assessment.progress'),
              icon: <BarChartOutlined />,
            },
          ],
        },
      ],
      preparation: [
        {
          key: 'preparation',
          label: t('menu:preparation.title'),
          type: 'group' as const,
          children: [
            {
              key: '/admin/preparation/overview',
              label: t('menu:preparation.overview'),
              icon: <DashboardOutlined />,
            },
            {
              key: '/admin/preparation/documents',
              label: t('menu:preparation.documents'),
              icon: <FileTextOutlined />,
            },
            {
              key: '/admin/preparation/checklist',
              label: t('menu:preparation.checklist'),
              icon: <CheckCircleOutlined />,
            },
          ],
        },
      ],
      issuance: [
        {
          key: 'issuance',
          label: t('menu:issuanceManagement.title'),
          type: 'group' as const,
          children: [
            {
              key: '/admin/issuance/console',
              label: t('menu:issuanceManagement.console'),
              icon: <RocketOutlined />,
            },
            {
              key: '/admin/issuance/workflow',
              label: t('menu:issuanceManagement.workflow'),
              icon: <BarChartOutlined />,
            },
            {
              key: '/admin/issuance/collaboration',
              label: t('menu:issuanceManagement.collaboration'),
              icon: <TeamOutlined />,
            },
          ],
        },
      ],
      'post-issuance': [
        {
          key: 'post-issuance',
          label: t('menu:postIssuance.title'),
          type: 'group' as const,
          children: [
            {
              key: '/admin/post/overview',
              label: t('menu:postIssuance.overview'),
              icon: <DashboardOutlined />,
            },
            {
              key: '/admin/post/funds',
              label: t('menu:postIssuance.funds'),
              icon: <DollarOutlined />,
            },
            {
              key: '/admin/post/reports',
              label: t('menu:postIssuance.reports'),
              icon: <FileTextOutlined />,
            },
          ],
        },
      ],
    };

    return configs[currentStage as keyof typeof configs] || [];
  };

  // 从Redux状态获取用户信息
  const { user, isAuthenticated } = useAppSelector(state => state.auth);

  // 调试信息
  console.log('SideNavigation - 用户状态:', {
    isAuthenticated,
    userRole: user?.role,
    userName: user?.name,
    userFromStorage:
      localStorage.getItem('user') || sessionStorage.getItem('user'),
  });

  // 根据用户角色判断菜单类型
  const isAdminUser = user?.role === 'admin';
  const isExchangeUser = user?.role === 'exchange';
  const isIssuerUser = user?.role === 'issuer';

  const menuItems = isAdminUser
    ? getAdminMenuItems()
    : isExchangeUser
      ? getExchangeMenuItems()
      : isIssuerUser
        ? getIssuerMenuItems()
        : getCompanyMenuItems(stage);

  // Bug修复: 改进菜单选中逻辑，支持路径前缀匹配
  const getSelectedKeys = () => {
    const pathname = location.pathname.replace(/\/$/, ''); // 移除末尾斜杠

    // 提取所有菜单key
    const allMenuKeys: string[] = [];
    const extractKeys = (items: any[]) => {
      items.forEach(item => {
        if (item.key && !item.type) allMenuKeys.push(item.key);
        if (item.children) extractKeys(item.children);
      });
    };
    extractKeys(menuItems);

    // 首先尝试精确匹配
    if (allMenuKeys.includes(pathname)) {
      return [pathname];
    }

    // 如果没有精确匹配，尝试前缀匹配（用于子路由）
    const matchedKey = allMenuKeys.find(key => pathname.startsWith(key + '/'));
    if (matchedKey) {
      return [matchedKey];
    }

    return [pathname];
  };

  const selectedKeys = getSelectedKeys();

  const handleMenuClick = ({ key }: { key: string }) => {
    // 发行人用户点击受限菜单时提示暂无权限
    if (isIssuerUser && restrictedMenuPaths.includes(key)) {
      message.warning(t('menu:message.noPermission'));
      return;
    }
    navigate(key);
  };

  return (
    <div
      style={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        background: 'transparent',
      }}
    >
      {/* 侧边栏标题 */}
      {!collapsed && (
        <div
          style={{
            padding: '24px 24px 16px',
            borderBottom: '1px solid rgba(0, 0, 0, 0.06)',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              marginBottom: '8px',
            }}
          >
            <div
              style={{
                width: '32px',
                height: '32px',
                borderRadius: '8px',
                background: 'linear-gradient(135deg, #52c41a, #1890ff)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <HomeOutlined style={{ color: 'white', fontSize: '16px' }} />
            </div>
            <div>
              <Text strong style={{ fontSize: '16px', color: '#262626' }}>
                {isAdminUser
                  ? t('common:sidebar.adminConsole')
                  : isExchangeUser
                    ? t('common:sidebar.exchangeWorkbench')
                    : isIssuerUser
                      ? t('common:sidebar.issuerCenter')
                      : t('common:sidebar.companyService')}
              </Text>
              <br />
              <Text type="secondary" style={{ fontSize: '12px' }}>
                {isAdminUser
                  ? t('common:sidebar.adminDesc')
                  : isExchangeUser
                    ? t('common:sidebar.exchangeDesc')
                    : isIssuerUser
                      ? t('common:sidebar.issuerDesc')
                      : t('common:sidebar.companyDesc')}
              </Text>
            </div>
          </div>
        </div>
      )}

      {/* 菜单内容 */}
      <div
        style={{
          flex: 1,
          padding: collapsed ? '16px 8px' : '16px 16px',
          overflow: 'auto',
        }}
      >
        <Menu
          mode="inline"
          selectedKeys={selectedKeys}
          items={menuItems}
          onClick={handleMenuClick}
          style={{
            border: 'none',
            background: 'transparent',
            fontSize: '14px',
          }}
          theme="light"
          inlineCollapsed={collapsed}
        />
      </div>

      {/* 底部版本信息 - 紧凑版 */}
      {!collapsed && (
        <div
          style={{
            padding: '12px 16px',
            borderTop: '1px solid rgba(16, 185, 129, 0.1)',
            background:
              'linear-gradient(135deg, rgba(16, 185, 129, 0.03) 0%, rgba(5, 150, 105, 0.01) 100%)',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: '4px',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
              }}
            >
              <div
                style={{
                  width: '18px',
                  height: '18px',
                  borderRadius: '4px',
                  background:
                    'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 1px 2px rgba(16, 185, 129, 0.2)',
                }}
              >
                <Text
                  style={{
                    color: 'white',
                    fontSize: '8px',
                    fontWeight: 'bold',
                  }}
                >
                  GB
                </Text>
              </div>
              <Text
                style={{
                  fontSize: '11px',
                  fontWeight: '600',
                  color: '#10b981',
                }}
              >
                {VersionInfo.displayVersion}
              </Text>
            </div>
            <div
              style={{
                padding: '1px 6px',
                borderRadius: '8px',
                background: VersionInfo.statusColors.bg,
                border: `1px solid ${VersionInfo.statusColors.border}`,
              }}
            >
              <Text
                style={{
                  fontSize: '8px',
                  color: VersionInfo.statusColors.text,
                  fontWeight: '500',
                }}
              >
                {VersionInfo.status}
              </Text>
            </div>
          </div>
          <Text
            type="secondary"
            style={{
              fontSize: '9px',
              color: '#9ca3af',
              lineHeight: '1.2',
            }}
          >
            {t('common:app.copyright', { year: new Date().getFullYear() })}
          </Text>
        </div>
      )}
    </div>
  );
};

export default SideNavigation;
